/**
 * "Receipt printer" in the tablet's sheet: which printer this till uses, and
 * whether it opens the cash drawer. Kept on the device (`printer.ts`): the
 * printer beside a till does not change with whoever signs in.
 *
 * In the till app there are three more: any receipt printer on the network,
 * one on USB, and one paired over Bluetooth (native.ts). A browser cannot
 * reach those, so they are only offered there.
 */

import { useState } from 'react';
import { Button, ErrorNotice, Field, Input, Segmented, Sheet } from '@restaurant/ui';

import { isTillApp, tillApp, TillAppError, type FoundPrinter } from './native';
import {
  APP_PRINTERS,
  PrinterError,
  describePrinter,
  loadPrinter,
  printerBase,
  savePrinter,
  sendToPrinter,
  type PrinterKind,
  type PrinterSettings,
} from './printer';

const KINDS: { value: PrinterKind; label: string }[] = [
  { value: 'browser', label: 'This device' },
  { value: 'epson', label: 'Epson' },
  { value: 'star', label: 'Star' },
];

const APP_KINDS: { value: PrinterKind; label: string }[] = [
  { value: 'network', label: 'Any brand' },
  { value: 'usb', label: 'USB' },
  { value: 'bluetooth', label: 'Bluetooth' },
];

export function PrinterRow() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadPrinter);
  return (
    <>
      <ul className="os__more pos__shift-rows">
        <li>
          <button type="button" className="os__more-item" onClick={() => setOpen(true)}>
            <span className="os__more-label">Receipt printer</span>
            <span className="os__more-hint">{describePrinter(settings)}</span>
          </button>
        </li>
      </ul>
      {open && (
        <PrinterSheet
          onDone={(next) => {
            setSettings(next);
            setOpen(false);
          }}
          onDismiss={() => setOpen(false)}
        />
      )}
    </>
  );
}

function PrinterSheet({
  onDone,
  onDismiss,
}: {
  onDone: (settings: PrinterSettings) => void;
  onDismiss: () => void;
}) {
  const inApp = isTillApp();
  const [draft, setDraft] = useState(loadPrinter);
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kinds = inApp ? [...KINDS, ...APP_KINDS] : KINDS;
  const base = printerBase(draft.address, inApp);
  const raw = APP_PRINTERS.includes(draft.kind);
  const ready =
    draft.kind === 'usb' || draft.kind === 'bluetooth' ? draft.device !== '' : base !== '';

  async function test() {
    setTesting(true);
    setTested(null);
    setError(null);
    try {
      await sendToPrinter(draft, {
        lines: [
          { text: 'OnRestaurant', align: 'center', big: true, bold: true },
          { text: 'Test print', align: 'center' },
          { text: new Date().toLocaleString(), align: 'center' },
        ],
        cut: true,
        drawer: false,
      });
      setTested('Printed. If nothing came out, check the paper.');
    } catch (e) {
      setError(e instanceof PrinterError ? e.message : 'The test print failed.');
    } finally {
      setTesting(false);
    }
  }

  return (
    <Sheet
      title="Receipt printer"
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          onClick={() => {
            savePrinter(draft);
            onDone(draft);
          }}
        >
          Save for this tablet
        </Button>
      }
    >
      <Segmented
        label="Which printer"
        options={kinds}
        value={draft.kind}
        scroll={inApp}
        onChange={(kind) => {
          // A USB printer's id means nothing to Bluetooth, and the other way round.
          const same = kind === draft.kind;
          setDraft({
            ...draft,
            kind,
            device: same ? draft.device : '',
            deviceName: same ? draft.deviceName : '',
          });
          setTested(null);
          setError(null);
        }}
      />

      {draft.kind === 'browser' ? (
        <p className="pr__note">
          Receipts go to whichever printer this device already uses, through its own print
          window. Any printer works. To open a cash drawer this way, set the printer&rsquo;s
          driver to kick it after printing.
        </p>
      ) : (
        <>
          {draft.kind === 'usb' || draft.kind === 'bluetooth' ? (
            <DevicePicker
              key={draft.kind}
              kind={draft.kind}
              chosen={draft.device}
              chosenName={draft.deviceName}
              onChoose={(printer) =>
                setDraft({ ...draft, device: printer.id, deviceName: printer.name })
              }
            />
          ) : (
            <Field
              label="Printer's address"
              hint={
                draft.kind === 'network'
                  ? 'Its IP address on your network, e.g. 192.168.1.40, printed on its self-test sheet. Port 9100 unless you add another, as 192.168.1.40:9101.'
                  : draft.kind === 'epson'
                    ? 'Its IP address on your network, e.g. 192.168.1.40. Printed on its status sheet.'
                    : 'Its IP address on your network. Printed on its self-test sheet.'
              }
            >
              <Input
                value={draft.address}
                inputMode="url"
                autoComplete="off"
                placeholder="192.168.1.40"
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
              />
            </Field>
          )}
          {!inApp && base.startsWith('https://') && (
            <p className="pr__note">
              Once per tablet, open{' '}
              <a href={base} target="_blank" rel="noreferrer">
                {base}
              </a>{' '}
              and accept the printer&rsquo;s certificate. Until then the till cannot reach it.
            </p>
          )}
          <Segmented
            label="Paper"
            options={[
              { value: '48', label: '80mm paper' },
              { value: '32', label: '58mm paper' },
            ]}
            value={String(draft.width)}
            onChange={(width) => setDraft({ ...draft, width: width === '32' ? 32 : 48 })}
          />
          {raw && (
            <>
              <Segmented
                label="Words"
                options={[
                  { value: 'text', label: 'As text' },
                  { value: 'image', label: 'As pictures' },
                ]}
                value={draft.text}
                onChange={(text) =>
                  setDraft({ ...draft, text: text === 'image' ? 'image' : 'text' })
                }
              />
              <p className="pr__note">
                As text is fastest. A line in a script the printer cannot spell (Arabic,
                Cyrillic, Chinese…) prints as a picture either way. If accented letters come out
                wrong, choose As pictures.
              </p>
            </>
          )}
          <label className="pr__check">
            <input
              type="checkbox"
              checked={draft.drawer}
              onChange={(e) => setDraft({ ...draft, drawer: e.target.checked })}
            />
            Open the cash drawer when cash is taken
          </label>
          <Button disabled={!ready} loading={testing} onClick={() => void test()}>
            Print a test
          </Button>
          {tested && (
            <p className="pr__ok" role="status">
              {tested}
            </p>
          )}
        </>
      )}
      {error && <ErrorNotice title="The test print did not go through" message={error} />}
    </Sheet>
  );
}

/** The USB printers plugged in, or the Bluetooth ones paired, as the app sees them. */
function DevicePicker({
  kind,
  chosen,
  chosenName,
  onChoose,
}: {
  kind: 'usb' | 'bluetooth';
  chosen: string;
  chosenName: string;
  onChoose: (printer: FoundPrinter) => void;
}) {
  const [found, setFound] = useState<FoundPrinter[] | null>(null);
  const [looking, setLooking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function look() {
    setLooking(true);
    setProblem(null);
    try {
      setFound(
        kind === 'usb' ? await tillApp.usbPrinters() : await tillApp.bluetoothPrinters(),
      );
    } catch (e) {
      setProblem(
        e instanceof TillAppError ? e.message : 'The tablet could not look for printers.',
      );
    } finally {
      setLooking(false);
    }
  }

  return (
    <>
      <p className="pr__note">
        {kind === 'usb'
          ? 'Plug the printer into the tablet and switch it on, then look for it.'
          : 'Pair the printer in the tablet’s Bluetooth settings first (its PIN is often 0000 or 1234), then look for it here.'}
      </p>
      {chosen && !found && (
        <p className="pr__ok">This tablet prints on {chosenName || chosen}.</p>
      )}
      <Button loading={looking} onClick={() => void look()}>
        {found ? 'Look again' : 'Find printers'}
      </Button>
      {found && found.length === 0 && (
        <p className="pr__note">
          {kind === 'usb'
            ? 'No USB printer is plugged in. Check its cable and that it is switched on.'
            : 'No paired printers. Pair it in the tablet’s Bluetooth settings, then look again.'}
        </p>
      )}
      {found && found.length > 0 && (
        <ul className="pr__found">
          {found.map((printer) => (
            <li key={printer.id}>
              <button
                type="button"
                className={`pr__device${printer.id === chosen ? ' is-chosen' : ''}`}
                aria-pressed={printer.id === chosen}
                onClick={() => onChoose(printer)}
              >
                <span className="pr__device-name">{printer.name}</span>
                <span className="pr__device-id">{printer.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {problem && <ErrorNotice title="Could not look for printers" message={problem} />}
    </>
  );
}
